// Supabase Client Configuration
// This file provides a shared Supabase client for the frontend applications

import { createClient } from '@supabase/supabase-js';
import { patchSessionGuard } from '../lib/session-guard';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
const supabasePublishableKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY || '';

if (!supabaseUrl || !supabasePublishableKey) {
  console.warn('Supabase URL or Publishable Key not configured. Using legacy API.');
}

export const supabase = createClient(supabaseUrl, supabasePublishableKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
  },
});

patchSessionGuard(supabase);

// ============================================================================
// AUTH TYPES
// ============================================================================

export interface RegisterInput {
  email: string;
  password: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
}

export interface LoginInput {
  email: string;
  password: string;
}

export interface UserProfile {
  id: string;
  account_id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  phone?: string;
  media?: { id: string; url: string; width?: number; height?: number } | null;
  role: string;
  is_vip?: boolean;
  vip_level?: string;
  is_developer?: boolean;
  is_enabled?: boolean;
  created_at: string;
  updated_at: string;
}

export interface AuthSession {
  user: {
    id: string;
    email?: string;
    user_metadata?: {
      role?: string;
      first_name?: string;
      last_name?: string;
      account_id?: string;
    };
  };
  profile?: UserProfile | null;
}

// ============================================================================
// AUTH SERVICE CLASS
// ============================================================================

export class AuthService {
  static isConfigured(): boolean {
    return !!(import.meta.env.VITE_SUPABASE_URL && import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY);
  }

  // Register new user
  static async register(input: RegisterInput): Promise<AuthSession> {
    if (!this.isConfigured()) {
      throw new Error('Supabase is not configured. Please check environment variables.');
    }

    const hostname = typeof window !== 'undefined' ? window.location.hostname : 'localhost';

    const { data, error } = await supabase.functions.invoke('register', {
      body: {
        ...input,
        hostname,
      },
    });

    if (error) throw error;
    if (data && typeof data === 'object' && 'error' in data) {
      throw new Error((data as any).error?.message || 'Registration failed');
    }

    // After registration, sign in the user
    const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
      email: input.email,
      password: input.password,
    });

    if (signInError) {
      throw signInError;
    }

    if (!signInData.user) {
      throw new Error('Failed to sign in after registration');
    }

    // Fetch user profile
    const profile = await this.fetchProfile(signInData.user.id);

    return {
      user: signInData.user,
      profile,
    };
  }

  // Login user
  static async login(input: LoginInput): Promise<AuthSession> {
    if (!this.isConfigured()) {
      throw new Error('Supabase is not configured. Please check environment variables.');
    }

    const { data, error } = await supabase.auth.signInWithPassword({
      email: input.email,
      password: input.password,
    });

    if (error) {
      throw error;
    }

    // Fetch user details from public.users table
    const profile = await this.fetchProfile(data.user.id);

    return {
      user: data.user,
      profile,
    };
  }

  // Logout
  static async logout(): Promise<void> {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  }

  // Get current user
  static async getCurrentUser(): Promise<AuthSession | null> {
    if (!this.isConfigured()) {
      return null;
    }

    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) {
      return null;
    }

    // Fetch user details from public.users table
    const profile = await this.fetchProfile(user.id);

    return {
      user,
      profile,
    };
  }

  // Get current user's profile
  static async getProfile(): Promise<UserProfile | null> {
    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) {
      return null;
    }

    return this.fetchProfile(user.id);
  }

  // Alias for getProfile - commonly used in the app
  static async getMe(): Promise<UserProfile> {
    const profile = await this.getProfile();
    if (!profile) {
      throw new Error('Not authenticated');
    }
    return profile;
  }

  // Fetch user profile from public.users table
  private static async fetchProfile(userId: string): Promise<UserProfile | null> {
    const { data, error } = await supabase.from('users').select('*').eq('id', userId).maybeSingle();

    if (error) {
      // PGRST116 (no rows) is expected for users without a profile yet
      if (error.code === 'PGRST116') {
        console.log('User profile not found in database, using minimal profile from auth');
        return null;
      }
      console.error('Error fetching user profile:', error);
      return null;
    }

    return data as UserProfile | null;
  }

  // Get current user's role
  static async getUserRole(): Promise<string | null> {
    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) {
      return null;
    }

    // Check metadata first
    const metadataRole = user.user_metadata?.role;
    if (metadataRole) {
      return metadataRole;
    }

    // Fallback to fetching from public.users table
    const profile = await this.fetchProfile(user.id);
    return profile?.role || null;
  }

  // Check if user has specific role
  static async hasRole(role: string): Promise<boolean> {
    const userRole = await this.getUserRole();
    return userRole === role;
  }

  // Check if user has any of the specified roles
  static async hasAnyRole(roles: string[]): Promise<boolean> {
    const userRole = await this.getUserRole();
    return roles.includes(userRole || '');
  }

  // Check if user is admin
  static async isAdmin(): Promise<boolean> {
    return this.hasRole('ADMIN');
  }

  static async isSuperAdmin(): Promise<boolean> {
    return this.hasRole('SUPER_ADMIN');
  }

  static async isMerchant(): Promise<boolean> {
    return this.hasRole('MERCHANT');
  }

  static async isStaff(): Promise<boolean> {
    return this.hasRole('STAFF');
  }

  static async isShopper(): Promise<boolean> {
    return this.hasRole('SHOPPER');
  }

  // Password reset
  static async forgotPassword(email: string): Promise<void> {
    if (!this.isConfigured()) {
      throw new Error('Supabase is not configured.');
    }

    const redirectTo =
      typeof window !== 'undefined'
        ? `${window.location.origin}/auth/reset-password`
        : 'https://app.social-flows.com/auth/reset-password';

    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo,
    });

    if (error) throw error;
  }

  static async requestPasswordReset(email: string): Promise<{ message: string }> {
    await this.forgotPassword(email);
    return { message: 'Password reset email sent' };
  }

  static async validateResetToken(_token: string): Promise<{ valid: boolean; email?: string }> {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (user) {
      return { valid: true, email: user.email };
    }
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (session) {
      return { valid: true, email: session.user.email };
    }
    return { valid: false };
  }

  static async resetPassword(_token: string, newPassword: string): Promise<{ message: string }> {
    await this.updatePassword(newPassword);
    return { message: 'Password updated successfully' };
  }

  // Update password
  static async updatePassword(newPassword: string): Promise<void> {
    if (!this.isConfigured()) {
      throw new Error('Supabase is not configured.');
    }

    const { error } = await supabase.auth.updateUser({
      password: newPassword,
    });

    if (error) throw error;
  }

  // Listen to auth state changes
  static onAuthStateChange(
    callback: (event: string, session: AuthSession | null) => void
  ): () => void {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event: string, session: any) => {
      if (session) {
        const profile = await this.fetchProfile(session.user.id);
        callback(event, { user: session.user, profile });
      } else {
        callback(event, null);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }

  // Update user profile
  static async updateProfile(updates: Partial<UserProfile>): Promise<UserProfile> {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('Not authenticated');
    }

    const { data, error } = await supabase
      .from('users')
      .update({
        first_name: updates.first_name,
        last_name: updates.last_name,
        phone: updates.phone,
        media: updates.media,
        updated_at: new Date().toISOString(),
      })
      .eq('id', user.id)
      .select()
      .single();

    if (error) throw error;

    return data as UserProfile;
  }

  // Sign in with OAuth provider
  static async signInWithOAuth(provider: 'google' | 'facebook' | 'github'): Promise<void> {
    const { error } = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo:
          typeof window !== 'undefined'
            ? `${window.location.origin}/auth/callback`
            : 'https://app.social-flows.com/auth/callback',
      },
    });

    if (error) throw error;
  }
}

// ============================================================================
// FALLBACK LEGACY API CLIENT (for transition period)
// ============================================================================

class LegacyAuthClient {
  private static get baseUrl(): string {
    return import.meta.env.VITE_API_URL || 'http://localhost:8080/api';
  }

  private static async fetch(endpoint: string, options?: RequestInit): Promise<Response> {
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      ...options,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
    });
    return response;
  }

  static async login(data: LoginInput): Promise<AuthSession> {
    const response = await this.fetch('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ identifier: data.email, password: data.password }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Login failed');
    }

    // For legacy API, we need to parse the token and create a session
    const loginResponse = await response.json();

    // Get user profile
    const profile = await this.getProfile();

    return {
      user: { id: profile.id, email: profile.email },
      profile,
    };
  }

  static async register(data: RegisterInput): Promise<AuthSession> {
    const response = await this.fetch('/auth/register', {
      method: 'POST',
      body: JSON.stringify({
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        phone: data.phone,
        password: data.password,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Registration failed');
    }

    const profile = await this.getProfile();

    return {
      user: { id: profile.id, email: profile.email },
      profile,
    };
  }

  static async logout(): Promise<void> {
    await this.fetch('/auth/logout', { method: 'POST' });
  }

  static async getCurrentUser(): Promise<AuthSession | null> {
    try {
      const profile = await this.getProfile();
      return {
        user: { id: profile.id, email: profile.email },
        profile,
      };
    } catch {
      return null;
    }
  }

  static async getProfile(): Promise<UserProfile> {
    const response = await this.fetch('/auth/me');

    if (!response.ok) {
      throw new Error('Failed to fetch user profile');
    }

    return response.json();
  }

  // Alias for getProfile - commonly used in the app
  static async getMe(): Promise<UserProfile> {
    return this.getProfile();
  }

  static async getUserRole(): Promise<string | null> {
    const profile = await this.getProfile();
    return profile.role || null;
  }

  static async hasRole(role: string): Promise<boolean> {
    const userRole = await this.getUserRole();
    return userRole === role;
  }

  static async isAdmin(): Promise<boolean> {
    return this.hasRole('ADMIN');
  }

  static async isMerchant(): Promise<boolean> {
    return this.hasRole('MERCHANT');
  }

  static async isShopper(): Promise<boolean> {
    return this.hasRole('SHOPPER');
  }

  static async requestPasswordReset(email: string): Promise<{ message: string }> {
    const response = await this.fetch('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
    return response.json();
  }

  static async resetPassword(token: string, newPassword: string): Promise<{ message: string }> {
    const response = await this.fetch('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, newPassword }),
    });
    return response.json();
  }

  static async validateResetToken(token: string): Promise<{ valid: boolean; email?: string }> {
    const response = await this.fetch('/auth/validate-reset-token', {
      method: 'POST',
      body: JSON.stringify({ token }),
    });
    return response.json();
  }
}

// Export auth service that automatically uses Supabase or falls back to legacy API
export const authService = AuthService.isConfigured()
  ? AuthService
  : (LegacyAuthClient as typeof AuthService & typeof LegacyAuthClient);
